// All item types matching server's ItemType enum (snake_case on wire)
export type ItemType =
  | 'wheat' | 'sheep' | 'chicken' | 'bronze' | 'iron' | 'gold' | 'wood' | 'fruit'
  | 'flour' | 'bread' | 'ale'
  | 'wool' | 'mutton' | 'leather' | 'cloth' | 'sails'
  | 'eggs' | 'feathers' | 'poultry' | 'arrows'
  | 'bronze_tools' | 'bronze_fittings' | 'ship_hardware'
  | 'iron_tools' | 'nails' | 'weapons' | 'chains' | 'armor' | 'ship_repair_kit'
  | 'jewelry' | 'gilded_decor'
  | 'planks' | 'charcoal' | 'barrels' | 'ship_parts'
  | 'wine' | 'preserves' | 'dried_fruit'

export const ALL_ITEMS: ItemType[] = [
  'wheat','sheep','chicken','bronze','iron','gold','wood','fruit',
  'flour','bread','ale',
  'wool','mutton','leather','cloth','sails',
  'eggs','feathers','poultry','arrows',
  'bronze_tools','bronze_fittings','ship_hardware',
  'iron_tools','nails','weapons','chains','armor','ship_repair_kit',
  'jewelry','gilded_decor',
  'planks','charcoal','barrels','ship_parts',
  'wine','preserves','dried_fruit',
]

export const RAW_ITEMS: ItemType[] = ['wheat','sheep','chicken','bronze','iron','gold','wood','fruit']

export const ITEM_DISPLAY_NAMES: Record<ItemType, string> = {
  wheat: 'Wheat', sheep: 'Sheep', chicken: 'Chicken', bronze: 'Bronze',
  iron: 'Iron', gold: 'Gold', wood: 'Wood', fruit: 'Fruit',
  flour: 'Flour', bread: 'Bread', ale: 'Ale',
  wool: 'Wool', mutton: 'Mutton', leather: 'Leather', cloth: 'Cloth', sails: 'Sails',
  eggs: 'Eggs', feathers: 'Feathers', poultry: 'Poultry', arrows: 'Arrows',
  bronze_tools: 'Bronze Tools', bronze_fittings: 'Bronze Fittings', ship_hardware: 'Ship Hardware',
  iron_tools: 'Iron Tools', nails: 'Nails', weapons: 'Weapons', chains: 'Chains',
  armor: 'Armor', ship_repair_kit: 'Ship Repair Kit',
  jewelry: 'Jewelry', gilded_decor: 'Gilded Decor',
  planks: 'Planks', charcoal: 'Charcoal', barrels: 'Barrels', ship_parts: 'Ship Parts',
  wine: 'Wine', preserves: 'Preserves', dried_fruit: 'Dried Fruit',
}

export const ITEM_BASE_PRICES: Record<ItemType, number> = {
  wheat: 5, sheep: 15, chicken: 8, bronze: 12, iron: 20, gold: 100, wood: 6, fruit: 7,
  flour: 10, bread: 15, ale: 20,
  wool: 8, mutton: 12, leather: 18, cloth: 25, sails: 80,
  eggs: 5, feathers: 4, poultry: 10, arrows: 15,
  bronze_tools: 30, bronze_fittings: 22, ship_hardware: 60,
  iron_tools: 45, nails: 6, weapons: 70, chains: 35, armor: 90, ship_repair_kit: 40,
  jewelry: 200, gilded_decor: 250,
  planks: 10, charcoal: 8, barrels: 25, ship_parts: 55,
  wine: 35, preserves: 12, dried_fruit: 10,
}

export type VillageProfileType = 'farming' | 'mining' | 'forestry' | 'port' | 'mountain'

// Server village configs: village index -> (server village_id, profile)
// Server has 30 markets (IDs 0-29). Client generates 30 villages, all with server markets.
export const VILLAGE_PROFILES: Array<{ serverId: number; profile: VillageProfileType }> = [
  { serverId: 0, profile: 'port' },
  { serverId: 1, profile: 'farming' },
  { serverId: 2, profile: 'mining' },
  { serverId: 3, profile: 'forestry' },
  { serverId: 4, profile: 'farming' },
  { serverId: 5, profile: 'mountain' },
  { serverId: 6, profile: 'port' },
  { serverId: 7, profile: 'mining' },
  { serverId: 8, profile: 'forestry' },
  { serverId: 9, profile: 'farming' },
  { serverId: 10, profile: 'mountain' },
  { serverId: 11, profile: 'port' },
  { serverId: 12, profile: 'farming' },
  { serverId: 13, profile: 'mining' },
  { serverId: 14, profile: 'forestry' },
  { serverId: 15, profile: 'farming' },
  { serverId: 16, profile: 'port' },
  { serverId: 17, profile: 'mountain' },
  { serverId: 18, profile: 'farming' },
  { serverId: 19, profile: 'mining' },
  { serverId: 20, profile: 'forestry' },
  { serverId: 21, profile: 'farming' },
  { serverId: 22, profile: 'port' },
  { serverId: 23, profile: 'mining' },
  { serverId: 24, profile: 'mountain' },
  { serverId: 25, profile: 'forestry' },
  { serverId: 26, profile: 'farming' },
  { serverId: 27, profile: 'port' },
  { serverId: 28, profile: 'mining' },
  { serverId: 29, profile: 'forestry' },
]

export const PROFILE_DISPLAY_NAMES: Record<VillageProfileType, string> = {
  farming: 'Farming Village',
  mining: 'Mining Village',
  forestry: 'Forestry Village',
  port: 'Port Town',
  mountain: 'Mountain Settlement',
}

export type VillagerSpecialty = 'farmer' | 'shepherd' | 'miner' | 'lumberjack' | 'fisherman' | 'craftsman' | 'smith'

export const SPECIALTY_DISPLAY: Record<VillagerSpecialty, string> = {
  farmer: 'Farmer', shepherd: 'Shepherd', miner: 'Miner',
  lumberjack: 'Lumberjack', fisherman: 'Fisherman',
  craftsman: 'Craftsman', smith: 'Smith',
}

export const SPECIALTY_GATHERS: Record<VillagerSpecialty, ItemType[]> = {
  farmer: ['wheat', 'fruit'],
  shepherd: ['sheep', 'chicken'],
  miner: ['iron', 'bronze', 'gold'],
  lumberjack: ['wood'],
  fisherman: ['fruit'], // future: fish
  craftsman: ['flour', 'planks', 'nails'],
  smith: ['bronze_tools', 'iron_tools', 'weapons', 'chains'],
}

export interface Villager {
  id: string
  name: string
  specialty: VillagerSpecialty
  assignedHouseIndex: number
  inventory: Partial<Record<ItemType, number>>
  gatherRate: number
  state: 'gathering' | 'idle' | 'trading' | 'sleeping'
}

export interface MarketPriceInfo {
  item: ItemType
  spot_price: number
  buy_10_cost: number | null
  sell_10_revenue: number | null
  pool_item_reserve: number
  pool_currency_reserve: number
}

export interface WalletInfo {
  follis: number
  solidus: number
  cargo: Array<[ItemType, number]>
  total_follis_value: number
}

export interface TradeResult {
  cost_follis: number
  new_follis_balance: number
  new_item_qty: number
  effective_price_per_unit: number
}

export interface SellResult {
  revenue_follis: number
  new_follis_balance: number
  new_item_qty: number
  effective_price_per_unit: number
}

export interface CraftResult {
  recipe_output: ItemType
  quantity_produced: number
  inputs_consumed: Array<[ItemType, number]>
}

export interface ExchangeResult {
  follis_delta: number
  solidus_delta: number
  new_follis: number
  new_solidus: number
}

export interface CraftingRecipe {
  inputs: Array<[ItemType, number]>
  outputs: Array<[ItemType, number]>
}

export const FOLLIS_PER_SOLIDUS = 120

// Supply level description
export function getSupplyLevel(reserve: number, baseSupply: number = 100): 'scarce' | 'low' | 'normal' | 'abundant' {
  const ratio = reserve / baseSupply
  if (ratio < 0.3) return 'scarce'
  if (ratio < 0.7) return 'low'
  if (ratio < 1.5) return 'normal'
  return 'abundant'
}

// Format currency
export function formatFollis(amount: number): string {
  return `${Math.floor(amount)}\u0192`
}

export function formatSolidus(amount: number): string {
  return `${Math.floor(amount)}s`
}

export function formatWallet(follis: number, solidus: number): string {
  const parts: string[] = []
  if (solidus > 0) parts.push(formatSolidus(solidus))
  parts.push(formatFollis(follis))
  return parts.join('  ')
}
